import type {
  BootstrapLatestUnreadEventPerFeed,
  BootstrapPage,
  BootstrapWorkspaceAccounts,
  BootstrapWorkspaceFeedGroupMemberships,
  BootstrapWorkspaceFeedGroups,
  BootstrapWorkspaceFeedItems,
  BootstrapWorkspaceFeedPermissions,
  BootstrapWorkspaceFeeds,
  BootstrapWorkspaceMemberships,
  BootstrapWorkspaceMembershipTags,
  BootstrapWorkspaceTags,
  BootstrapWorkspaceTemplates,
  Transport,
  WorkspaceMembership,
  WsAccount,
  WsCommandAlias,
  WsEvent,
  WsFeed,
  WsFeedGroup,
  WsFeedGroupMembership,
  WsItem,
  WsMembershipAvailability,
  WsPermission,
  WsTemplate,
} from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import * as endpoints from "@storyboard/web-client";
import { transport } from "@/config";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import {
  bulkUpsertAccount,
  bulkUpsertFeed,
  bulkUpsertFeedGroup,
  bulkUpsertFeedGroupMembership,
  bulkUpsertItemAndCalculateMembership,
  bulkUpsertPermission,
  bulkUpsertTag,
  bulkUpsertTemplate,
  bulkUpsertWorkspaceCommandAlias,
  bulkUpsertWorkspaceMembership,
  bulkUpsertWorkspaceMembershipTag,
  bulkUpsertWsMembershipAvailability,
} from "@/models/repositories";
import { bulkUpsertAccountEvent } from "@/models/repositories/AccountEvent";
import { bulkUpsertFeedPreference } from "@/models/repositories/FeedPreference";
import { databaseResetService } from "@/services/DatabaseReset";

/** Matches apiv2 `BOOTSTRAP_MAX_PAGE_SIZE` — keep in sync when either changes. */
export const BOOTSTRAP_MAX_PAGE_SIZE = 10_000;

export type BootstrapResponses =
  | BootstrapWorkspaceFeedItems
  | BootstrapWorkspaceMemberships
  | BootstrapWorkspaceMembershipTags
  | BootstrapWorkspaceFeedPermissions
  | BootstrapWorkspaceFeeds
  | BootstrapWorkspaceTags
  | BootstrapWorkspaceTemplates
  | BootstrapWorkspaceAccounts
  | BootstrapWorkspaceFeedGroups
  | BootstrapWorkspaceFeedGroupMemberships
  | BootstrapLatestUnreadEventPerFeed;

export type CreateManyPayloads =
  | WorkspaceMembership[]
  | WsItem[]
  | WsPermission[]
  | WsFeed[]
  | WsTemplate[]
  | WsAccount[]
  | WsCommandAlias[]
  | WsFeedGroup[]
  | WsFeedGroupMembership[]
  | WsEvent[]
  | WsMembershipAvailability[];

export type BootstrapEventListType = {
  /** Stable identifier: registry cursor key and telemetry label. Keep in sync with `fetchPage`. */
  bootstrapFunctionName: string;
  fetchPage: (transport: Transport, params: BootstrapPage & { mine?: boolean }) => Promise<BootstrapResponses>;
  bootstrapFunctionCallResponseName: string;
  createManyFunctionName: (values: any) => Promise<unknown>;
  tableName: string;
  feed?: boolean;
  wait?: boolean;
  pageSize?: number;
  mine?: boolean;
  recursive?: boolean;
};

const bulkUpsertItemsBootstrap = (values: WsItem[]) => bulkUpsertItemAndCalculateMembership(values, {});

const bulkUpsertAvailabilityBootstrap = (values: WsMembershipAvailability[]) =>
  bulkUpsertWsMembershipAvailability(values);

export const bootstrapItem = async ({
  bootstrapEvent,
  workspaceId,
  page,
  pageSize,
  mine,
  timestamp,
}: {
  bootstrapEvent: BootstrapEventListType;
  workspaceId: string;
  page: number;
  pageSize: number;
  mine: boolean;
  timestamp?: string;
}) => {
  try {
    const additionalResponse: BootstrapResponses = await bootstrapEvent.fetchPage(transport, {
      workspaceId,
      page,
      pageSize,
      mine,
      timestamp,
    });
    const responseData = (additionalResponse as unknown as Record<string, CreateManyPayloads | undefined>)[
      bootstrapEvent?.bootstrapFunctionCallResponseName
    ];
    // Prefer the server's effective pageSize — it may clamp below the requested value.
    const effectivePageSize =
      "pageSize" in additionalResponse && typeof additionalResponse.pageSize === "number"
        ? additionalResponse.pageSize
        : pageSize;
    log.info({
      key: bootstrapEvent?.bootstrapFunctionCallResponseName ?? "missing-name",
      count: responseData?.length ?? 0,
    });
    return { data: responseData, pageSize: effectivePageSize };
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * The arguments for the page after `args`: every request parameter of the walk's first page carried forward, so a
 * filter such as `mine` holds on every page rather than on page 0 alone.
 *
 * @param args - The current page's arguments.
 * @param pageSize - The page size the next request asks for, which the server may have clamped below `args.pageSize`.
 * @returns `args` with `page` advanced by one and `pageSize` replaced.
 */
export function nextBootstrapPageArgs<T extends { page: number; pageSize: number }>(args: T, pageSize: number): T {
  return { ...args, page: args.page + 1, pageSize };
}

/**
 * Recursive api call that loads bootstrap event data and hydrates local db
 * If the response length matches the page size, it calls itself with the next page number
 * @param workspaceId
 * @param bootstrapEvent
 * @param pageSize
 * @param page
 */
export const loadBootstrapEventAndHydrateLocalDB = async ({
  workspaceId,
  bootstrapEvent,
  pageSize,
  page,
  moreData,
}: {
  workspaceId: string;
  bootstrapEvent: BootstrapEventListType;
  pageSize: number;
  page: number;
  moreData?: any[];
}) => {
  const payload = {
    bootstrapEvent,
    workspaceId,
    page,
    pageSize,
    mine: true,
    timestamp: undefined,
  };

  try {
    const { data: response, pageSize: effectivePageSize } = await bootstrapItem(payload);
    const pageSizeForHasMore = effectivePageSize < pageSize ? effectivePageSize : pageSize;
    if (response && response.length > 0) {
      await bootstrapEvent.createManyFunctionName(response);
    }
    if (response?.length === pageSizeForHasMore) {
      const combined = moreData ? [...moreData, ...response] : response;
      return await loadBootstrapEventAndHydrateLocalDB({
        bootstrapEvent,
        workspaceId,
        page: page + 1,
        pageSize: pageSizeForHasMore,
        moreData: combined,
      });
    } else if (moreData) {
      const moreDataArray = Array.isArray(moreData) ? moreData : [];
      const responseDataArray = Array.isArray(response) ? response : [];
      return [...moreDataArray, ...responseDataArray];
    } else {
      return response;
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

export const bootstrapWorkspaceMemberships = {
  bootstrapFunctionName: "bootstrapWorkspaceMemberships",
  fetchPage: endpoints.bootstrapWorkspaceMemberships,
  createManyFunctionName: bulkUpsertWorkspaceMembership,
  bootstrapFunctionCallResponseName: "workspaceMemberships",
  tableName: "workspaceMembership",
};

export const bootstrapWorkspaceFeedPermissions = {
  bootstrapFunctionName: "bootstrapWorkspaceFeedPermissions",
  fetchPage: endpoints.bootstrapWorkspaceFeedPermissions,
  createManyFunctionName: bulkUpsertPermission,
  bootstrapFunctionCallResponseName: "permissions",
  tableName: "permission",
  mine: true,
};

export const bootstrappedApiCalls: BootstrapEventListType[] = [
  {
    bootstrapFunctionName: "bootstrapWorkspaceMembershipAvailabilities",
    fetchPage: endpoints.bootstrapWorkspaceMembershipAvailabilities,
    createManyFunctionName: bulkUpsertAvailabilityBootstrap,
    bootstrapFunctionCallResponseName: "membershipAvailabilities",
    tableName: "wsMembershipAvailability",
    mine: true,
  },
  bootstrapWorkspaceFeedPermissions,
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeeds",
    fetchPage: endpoints.bootstrapWorkspaceFeeds,
    createManyFunctionName: bulkUpsertFeed,
    bootstrapFunctionCallResponseName: "feeds",
    tableName: "feed",
    feed: true,
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceNonAliasFeedLatestItems",
    fetchPage: endpoints.bootstrapWorkspaceNonAliasFeedLatestItems,
    createManyFunctionName: bulkUpsertItemsBootstrap,
    bootstrapFunctionCallResponseName: "items",
    tableName: "item",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceAliasFeedLatestOwnerItems",
    fetchPage: endpoints.bootstrapWorkspaceAliasFeedLatestOwnerItems,
    createManyFunctionName: bulkUpsertItemsBootstrap,
    bootstrapFunctionCallResponseName: "items",
    tableName: "item",
  },
  bootstrapWorkspaceMemberships,
  {
    bootstrapFunctionName: "bootstrapWorkspaceTags",
    fetchPage: endpoints.bootstrapWorkspaceTags,
    createManyFunctionName: bulkUpsertTag,
    bootstrapFunctionCallResponseName: "tags",
    tableName: "tag",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceMembershipTags",
    fetchPage: endpoints.bootstrapWorkspaceMembershipTags,
    createManyFunctionName: bulkUpsertWorkspaceMembershipTag,
    bootstrapFunctionCallResponseName: "workspaceMembershipTags",
    tableName: "workspaceMembershipTag",
    recursive: false,
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceTemplates",
    fetchPage: endpoints.bootstrapWorkspaceTemplates,
    createManyFunctionName: bulkUpsertTemplate,
    bootstrapFunctionCallResponseName: "templates",
    tableName: "template",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceAccounts",
    fetchPage: endpoints.bootstrapWorkspaceAccounts,
    createManyFunctionName: bulkUpsertAccount,
    bootstrapFunctionCallResponseName: "accounts",
    tableName: "account",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeedGroups",
    fetchPage: endpoints.bootstrapWorkspaceFeedGroups,
    createManyFunctionName: bulkUpsertFeedGroup,
    bootstrapFunctionCallResponseName: "feedGroups",
    tableName: "feedGroup",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeedGroupMemberships",
    fetchPage: endpoints.bootstrapWorkspaceFeedGroupMemberships,
    createManyFunctionName: bulkUpsertFeedGroupMembership,
    bootstrapFunctionCallResponseName: "feedGroupMemberships",
    tableName: "feedGroupMembership",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceCommandAliases",
    fetchPage: endpoints.bootstrapWorkspaceCommandAliases,
    createManyFunctionName: bulkUpsertWorkspaceCommandAlias,
    bootstrapFunctionCallResponseName: "commandAliases",
    tableName: "workspaceCommandAlias",
  },
  {
    bootstrapFunctionName: "latestUnreadEventPerFeed",
    fetchPage: endpoints.latestUnreadEventPerFeed,
    createManyFunctionName: bulkUpsertAccountEvent,
    bootstrapFunctionCallResponseName: "events",
    tableName: "accountEvent",
  },
  {
    bootstrapFunctionName: "latestUnreadEventPerFeedForOrganizers",
    fetchPage: endpoints.latestUnreadEventPerFeedForOrganizers,
    createManyFunctionName: bulkUpsertAccountEvent,
    bootstrapFunctionCallResponseName: "events",
    tableName: "accountEvent",
  },
  {
    bootstrapFunctionName: "bootstrapWorkspaceFeedPreferences",
    fetchPage: endpoints.bootstrapWorkspaceFeedPreferences,
    createManyFunctionName: bulkUpsertFeedPreference,
    bootstrapFunctionCallResponseName: "feedPreferences",
    tableName: "feedPreference",
    mine: true,
  },
];

/** The event's stored cursor, or `undefined` when there is none or the registry is unreadable. */
export const fetchLatestBootstrapEventDate = async (name: string): Promise<Date | undefined> =>
  await BootstrapEventsRegistry.getInstance().getLatestEventDate(name);

/**
 * Records the bootstrap cursor for `name`. Best-effort: an unwritable registry (slow IndexedDB
 * open, blocked by another tab) costs the next load an incremental boot, not this one's rows, so a
 * write failure must not reject the bootstrap that already fetched them (WEB-2245).
 *
 * @param recordedAt - The instant the cursor stands at: the server's clock as the entry's first request went out,
 *   so a row changed while its pages were read is inside the next delta. The client's clock, now, when absent.
 */
export const insertBootstrapEvent = async (name: string, tableName: string, recordedAt?: Date): Promise<void> => {
  try {
    await BootstrapEventsRegistry.getInstance().markBootstrap(name, tableName, undefined, recordedAt);
  } catch (error) {
    log.warn("Failed to record bootstrap event; next load will bootstrap fully", { name, tableName, error });
  }
};

/** Hard-reboots the app: wipes the local snapshot and registry, then reloads. */
export const forceReload = async (): Promise<void> => {
  try {
    await databaseResetService.hardReboot();
  } catch {
    return Promise.reject({ message: "Error forcing reload" });
  }
};
